import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc
} from "firebase/firestore";
import { db } from "../firebase";
import menuItems from "../constants/menuItems";
import Navbar from "../components/Navbar";

const ROOM_LIMIT = 30;
const RESERVATION_LIMIT = 5;
const PAYMENT_LIMIT = 5;

function PriceListAdm({ userEmail, onLogout, userRole }) {
  const [showPriceListPopup, setShowPriceListPopup] = useState(false);
  const [showReservationPopup, setShowReservationPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [editGroupModal, setEditGroupModal] = useState({
    open: false,
    originalGroupName: "",
    groupName: "",
    price: "",
    applyPerPerson: false
  });
  const [roomItems, setRoomItems] = useState([]);
  const [reservationItems, setReservationItems] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [reservationEdits, setReservationEdits] = useState({});
  const [paymentEdits, setPaymentEdits] = useState({});
  const [roomForm, setRoomForm] = useState({
    groupName: "",
    selectedGroup: "",
    name: "",
    price: "",
    applyPerPerson: false,
    personLimit: ""
  });
  const [roomEdits, setRoomEdits] = useState({});
  const [status, setStatus] = useState({ type: "", message: "" });
  const [reservationStatus, setReservationStatus] = useState({
    type: "",
    message: ""
  });
  const [paymentStatus, setPaymentStatus] = useState({
    type: "",
    message: ""
  });
  const [savingRoom, setSavingRoom] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [savingReservation, setSavingReservation] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "priceList"), (snapshot) => {
      const rooms = [];
      const reservations = [];
      const payments = [];
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const type = data.type || (docSnap.id === "reservation" ? "reservation" : "room");
        if (type === "reservation") {
          reservations.push({ id: docSnap.id, ...data });
        } else if (type === "payment") {
          payments.push({ id: docSnap.id, ...data });
        } else {
          rooms.push({ id: docSnap.id, ...data });
        }
      });
      setRoomItems(rooms);
      setReservationItems(reservations);
      setPaymentMethods(payments);
    });

    return () => unsubscribe();
  }, []);

  const roomCount = roomItems.length;
  const isRoomLimitReached = roomCount >= ROOM_LIMIT;
  const groupOptions = useMemo(() => {
    const names = new Set();
    roomItems.forEach((room) => {
      const groupName = String(room.groupName || "").trim();
      if (groupName) {
        names.add(groupName);
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [roomItems]);

  const handleRoomFormChange = (event) => {
    const { name, type, value, checked } = event.target;
    setRoomForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleAddRoom = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });

    const nextGroupName = roomForm.groupName.trim() || roomForm.selectedGroup.trim();

    if (!nextGroupName) {
      setStatus({ type: "error", message: "Wybierz lub wpisz nazwę kategorii." });
      return;
    }

    if (!roomForm.name.trim()) {
      setStatus({ type: "error", message: "Podaj nazwę pokoju." });
      return;
    }

    if (isRoomLimitReached) {
      setStatus({
        type: "error",
        message: `Limit ${ROOM_LIMIT} pozycji został osiągnięty.`
      });
      return;
    }

    const price = Number(roomForm.price);
    const applyPerPerson = Boolean(roomForm.applyPerPerson);
    const personLimit = Number(roomForm.personLimit);

    if (Number.isNaN(price)) {
      setStatus({ type: "error", message: "Podaj poprawną cenę." });
      return;
    }

    if (Number.isNaN(personLimit) || personLimit <= 0) {
      setStatus({ type: "error", message: "Podaj poprawny limit osób." });
      return;
    }

    setSavingRoom(true);
    try {
      await addDoc(collection(db, "priceList"), {
        type: "room",
        groupName: nextGroupName,
        name: roomForm.name.trim(),
        price,
        applyPerPerson,
        personLimit,
        createdAt: serverTimestamp()
      });
      setRoomForm({
        groupName: "",
        selectedGroup: "",
        name: "",
        price: "",
        applyPerPerson: false,
        personLimit: ""
      });
      setStatus({ type: "success", message: "Dodano pozycję." });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Nie udało się dodać pozycji."
      });
    } finally {
      setSavingRoom(false);
    }
  };

  const handleRoomEditChange = (id, field, value) => {
    setRoomEdits((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleRoomUpdate = async (room) => {
    const edits = roomEdits[room.id] || {};
    const nextName = (edits.name ?? room.name ?? "").trim();
    const personLimit = Number(edits.personLimit ?? room.personLimit ?? 0);

    if (!nextName) {
      setStatus({ type: "error", message: "Nazwa nie może być pusta." });
      return;
    }

    if (Number.isNaN(personLimit) || personLimit <= 0) {
      setStatus({ type: "error", message: "Podaj poprawny limit osób." });
      return;
    }

    setSavingRoom(true);
    try {
      await updateDoc(doc(db, "priceList", room.id), {
        name: nextName,
        personLimit
      });
      setRoomEdits((prev) => {
        const next = { ...prev };
        delete next[room.id];
        return next;
      });
      setStatus({ type: "success", message: "Zapisano zmiany." });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Nie udało się zapisać zmian."
      });
    } finally {
      setSavingRoom(false);
    }
  };

  const openEditGroupModal = (groupName) => {
    const groupRooms = roomItems.filter(
      (room) => String(room.groupName || "").trim() === groupName
    );
    const groupSample = groupRooms[0] || null;
    if (!groupSample) {
      return;
    }
    setEditGroupModal({
      open: true,
      originalGroupName: groupName,
      groupName,
      price: Number(groupSample.price) || 0,
      applyPerPerson: Boolean(groupSample.applyPerPerson)
    });
  };

  const handleEditGroupModalChange = (event) => {
    const { name, type, value, checked } = event.target;
    setEditGroupModal((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleEditGroupSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });

    const nextGroupName = editGroupModal.groupName.trim();
    const price = Number(editGroupModal.price);
    const applyPerPerson = Boolean(editGroupModal.applyPerPerson);

    if (!nextGroupName) {
      setStatus({ type: "error", message: "Nazwa kategorii nie może być pusta." });
      return;
    }

    if (Number.isNaN(price)) {
      setStatus({ type: "error", message: "Podaj poprawną cenę." });
      return;
    }

    const roomsInGroup = roomItems.filter(
      (room) => String(room.groupName || "").trim() === editGroupModal.originalGroupName
    );

    if (roomsInGroup.length === 0) {
      setStatus({ type: "error", message: "Nie znaleziono pokoi dla tej kategorii." });
      return;
    }

    setSavingGroup(true);
    try {
      await Promise.all(
        roomsInGroup.map((room) =>
          updateDoc(doc(db, "priceList", room.id), {
            groupName: nextGroupName,
            price,
            applyPerPerson
          })
        )
      );
      setEditGroupModal({
        open: false,
        originalGroupName: "",
        groupName: "",
        price: "",
        applyPerPerson: false
      });
      setStatus({ type: "success", message: "Zapisano ustawienia kategorii." });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Nie udało się zapisać ustawień kategorii."
      });
    } finally {
      setSavingGroup(false);
    }
  };

  const handleRoomDelete = async (roomId) => {
    setStatus({ type: "", message: "" });
    setSavingRoom(true);
    try {
      await deleteDoc(doc(db, "priceList", roomId));
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Nie udało się usunąć pozycji."
      });
    } finally {
      setSavingRoom(false);
    }
  };

  const reservationCount = reservationItems.length;
  const isReservationLimitReached = reservationCount >= RESERVATION_LIMIT;
  const paymentCount = paymentMethods.length;
  const isPaymentLimitReached = paymentCount >= PAYMENT_LIMIT;

  const handleAddReservationItem = async (values) => {
    setReservationStatus({ type: "", message: "" });

    if (!values.name.trim()) {
      setReservationStatus({ type: "error", message: "Podaj nazwę pozycji." });
      return false;
    }

    if (isReservationLimitReached) {
      setReservationStatus({
        type: "error",
        message: `Limit ${RESERVATION_LIMIT} pozycji został osiągnięty.`
      });
      return false;
    }

    const price = Number(values.price);
    const applyPerDay = Boolean(values.applyPerDay);
    const applyPerPerson = Boolean(values.applyPerPerson);

    if (Number.isNaN(price)) {
      setReservationStatus({ type: "error", message: "Podaj poprawną cenę." });
      return false;
    }

    setSavingReservation(true);
    try {
      await addDoc(collection(db, "priceList"), {
        type: "reservation",
        name: values.name.trim(),
        price,
        applyPerDay,
        applyPerPerson,
        createdAt: serverTimestamp()
      });
      setReservationStatus({ type: "success", message: "Dodano pozycję." });
      return true;
    } catch (error) {
      setReservationStatus({
        type: "error",
        message: error.message || "Nie udało się zapisać pozycji."
      });
      return false;
    } finally {
      setSavingReservation(false);
    }
  };

  const handleReservationEditChange = (id, field, value) => {
    setReservationEdits((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleAddPaymentMethod = async (values) => {
    setPaymentStatus({ type: "", message: "" });

    if (!values.name.trim()) {
      setPaymentStatus({ type: "error", message: "Podaj nazwę metody." });
      return false;
    }

    if (isPaymentLimitReached) {
      setPaymentStatus({
        type: "error",
        message: `Limit ${PAYMENT_LIMIT} pozycji został osiągnięty.`
      });
      return false;
    }

    const depositPercent = Number(values.depositPercent);
    const paymentDueDays = Number(values.paymentDueDays);

    if (Number.isNaN(depositPercent) || depositPercent < 0) {
      setPaymentStatus({ type: "error", message: "Podaj poprawny procent zadatku." });
      return false;
    }

    if (Number.isNaN(paymentDueDays) || paymentDueDays < 0) {
      setPaymentStatus({ type: "error", message: "Podaj poprawny termin płatności." });
      return false;
    }

    setSavingPayment(true);
    try {
      await addDoc(collection(db, "priceList"), {
        type: "payment",
        name: values.name.trim(),
        depositPercent,
        paymentDueDays,
        isDefault: false,
        createdAt: serverTimestamp()
      });
      setPaymentStatus({ type: "success", message: "Dodano metodę płatności." });
      return true;
    } catch (error) {
      setPaymentStatus({
        type: "error",
        message: error.message || "Nie udało się zapisać metody."
      });
      return false;
    } finally {
      setSavingPayment(false);
    }
  };

  const handlePaymentEditChange = (id, field, value) => {
    setPaymentEdits((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };


  const handlePaymentUpdate = async (item) => {
    const edits = paymentEdits[item.id] || {};
    const nextName = (edits.name ?? item.name ?? "").trim();
    const depositPercent = Number(edits.depositPercent ?? item.depositPercent ?? 0);
    const paymentDueDays = Number(edits.paymentDueDays ?? item.paymentDueDays ?? 0);
    const isDefault = Boolean(edits.isDefault ?? item.isDefault);

    if (!nextName) {
      setPaymentStatus({ type: "error", message: "Nazwa nie może być pusta." });
      return;
    }

    if (Number.isNaN(depositPercent) || depositPercent < 0) {
      setPaymentStatus({ type: "error", message: "Podaj poprawny procent zadatku." });
      return;
    }

    if (Number.isNaN(paymentDueDays) || paymentDueDays < 0) {
      setPaymentStatus({ type: "error", message: "Podaj poprawny termin płatności." });
      return;
    }

    setSavingPayment(true);
    try {
      if (isDefault) {
        const resetDefaults = paymentMethods
          .filter((method) => method.id !== item.id && method.isDefault)
          .map((method) =>
            updateDoc(doc(db, "priceList", method.id), { isDefault: false })
          );
        await Promise.all(resetDefaults);
      }
      await updateDoc(doc(db, "priceList", item.id), {
        name: nextName,
        depositPercent,
        paymentDueDays,
        isDefault
      });
      setPaymentEdits((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setPaymentStatus({ type: "success", message: "Zapisano zmiany." });
    } catch (error) {
      setPaymentStatus({
        type: "error",
        message: error.message || "Nie udało się zapisać zmian."
      });
    } finally {
      setSavingPayment(false);
    }
  };

  const handlePaymentDelete = async (itemId) => {
    setPaymentStatus({ type: "", message: "" });
    setSavingPayment(true);
    try {
      await deleteDoc(doc(db, "priceList", itemId));
    } catch (error) {
      setPaymentStatus({
        type: "error",
        message: error.message || "Nie udało się usunąć metody."
      });
    } finally {
      setSavingPayment(false);
    }
  };

  const handleReservationUpdate = async (item) => {
    const edits = reservationEdits[item.id] || {};
    const defaults = getReservationItemDefaults(item);
    const nextName = (edits.name ?? item.name ?? "").trim();
    const price = Number(edits.price ?? defaults.price ?? 0);
    const applyPerDay = Boolean(
      edits.applyPerDay ?? defaults.applyPerDay
    );
    const applyPerPerson = Boolean(
      edits.applyPerPerson ?? defaults.applyPerPerson
    );

    if (!nextName) {
      setReservationStatus({ type: "error", message: "Nazwa nie może być pusta." });
      return;
    }

    if (Number.isNaN(price)) {
      setReservationStatus({ type: "error", message: "Podaj poprawną cenę." });
      return;
    }

    setSavingReservation(true);
    try {
      await updateDoc(doc(db, "priceList", item.id), {
        name: nextName,
        price,
        applyPerDay,
        applyPerPerson
      });
      setReservationEdits((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setReservationStatus({ type: "success", message: "Zapisano zmiany." });
    } catch (error) {
      setReservationStatus({
        type: "error",
        message: error.message || "Nie udało się zapisać zmian."
      });
    } finally {
      setSavingReservation(false);
    }
  };

  const handleReservationDelete = async (itemId) => {
    setReservationStatus({ type: "", message: "" });
    setSavingReservation(true);
    try {
      await deleteDoc(doc(db, "priceList", itemId));
    } catch (error) {
      setReservationStatus({
        type: "error",
        message: error.message || "Nie udało się usunąć pozycji."
      });
    } finally {
      setSavingReservation(false);
    }
  };

  const getReservationItemDefaults = (item) => {
    const legacyPerDay = Number(item?.pricePerReservation);
    const legacyPerPerson = Number(item?.pricePerPerson);
    const applyPerDay = typeof item?.applyPerDay === "boolean"
      ? item.applyPerDay
      : Number.isFinite(legacyPerDay) && legacyPerDay > 0;
    const applyPerPerson = typeof item?.applyPerPerson === "boolean"
      ? item.applyPerPerson
      : Number.isFinite(legacyPerPerson) && legacyPerPerson > 0;
    const price = Number(item?.price);
    const resolvedPrice = Number.isFinite(price)
      ? price
      : Number.isFinite(legacyPerDay)
        ? legacyPerDay
        : Number.isFinite(legacyPerPerson)
          ? legacyPerPerson
          : 0;

    return {
      price: resolvedPrice,
      applyPerDay,
      applyPerPerson
    };
  };

  const getRoomItemDefaults = (room) => {
    const legacyPerDay = Number(room?.pricePerDay);
    const legacyPerPerson = Number(room?.pricePerPerson);
    const applyPerPerson = typeof room?.applyPerPerson === "boolean"
      ? room.applyPerPerson
      : Number.isFinite(legacyPerPerson) && legacyPerPerson > 0;
    const price = Number(room?.price);
    const resolvedPrice = Number.isFinite(price)
      ? price
      : Number.isFinite(legacyPerDay)
        ? legacyPerDay
        : Number.isFinite(legacyPerPerson)
          ? legacyPerPerson
          : 0;

    return {
      price: resolvedPrice,
      applyPerPerson
    };
  };

  const roomRows = useMemo(
    () =>
      roomItems.map((room) => {
        const edits = roomEdits[room.id] || {};
        const defaults = getRoomItemDefaults(room);
        return {
          ...room,
          editGroupName: edits.groupName ?? room.groupName ?? "",
          editName: edits.name ?? room.name ?? "",
          editPrice: edits.price ?? defaults.price ?? 0,
          editApplyPerPerson: edits.applyPerPerson ?? defaults.applyPerPerson,
          editPersonLimit: edits.personLimit ?? room.personLimit ?? 0
        };
      }),
    [roomItems, roomEdits]
  );

  const roomRowsByGroup = useMemo(() => {
    const grouped = new Map();
    roomRows.forEach((room) => {
      const groupName = String(room.editGroupName || room.groupName || "").trim() || "Bez kategorii";
      if (!grouped.has(groupName)) {
        grouped.set(groupName, []);
      }
      grouped.get(groupName).push(room);
    });
    return Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [roomRows]);

  const reservationRows = useMemo(
    () =>
      reservationItems.map((item) => {
        const edits = reservationEdits[item.id] || {};
        const defaults = getReservationItemDefaults(item);
        return {
          ...item,
          editName: edits.name ?? item.name ?? "",
          editPrice: edits.price ?? defaults.price ?? 0,
          editApplyPerDay: edits.applyPerDay ?? defaults.applyPerDay,
          editApplyPerPerson: edits.applyPerPerson ?? defaults.applyPerPerson
        };
      }),
    [reservationItems, reservationEdits]
  );

  const paymentRows = useMemo(
    () =>
      paymentMethods.map((item) => {
        const edits = paymentEdits[item.id] || {};
        return {
          ...item,
          editName: edits.name ?? item.name ?? "",
          editDepositPercent: edits.depositPercent ?? item.depositPercent ?? 0,
          editPaymentDueDays: edits.paymentDueDays ?? item.paymentDueDays ?? 0,
          editIsDefault: edits.isDefault ?? item.isDefault ?? false
        };
      }),
    [paymentMethods, paymentEdits]
  );


  return (
    <div className="HomeShell PriceListPage">
      {/* Popup/modal for adding a new group and rooms */}
      {showPriceListPopup && (
        <ModalAddGroup
          onClose={() => setShowPriceListPopup(false)}
          onSaved={() => setShowPriceListPopup(false)}
        />
      )}

      {showReservationPopup && (
        <ModalAddReservationItem
          onClose={() => setShowReservationPopup(false)}
          onSave={handleAddReservationItem}
          saving={savingReservation}
        />
      )}

      {showPaymentPopup && (
        <ModalAddPaymentMethod
          onClose={() => setShowPaymentPopup(false)}
          onSave={handleAddPaymentMethod}
          saving={savingPayment}
        />
      )}

      {editGroupModal.open && (
        <div className="ModalOverlay" onClick={() => setEditGroupModal((prev) => ({ ...prev, open: false }))}>
          <div className="ModalPanel" onClick={(event) => event.stopPropagation()}>
            <div className="ModalHeader">
              <h2 className="SectionTitle">Edytuj Kategorię</h2>
              <button
                className="Button Ghost"
                type="button"
                onClick={() => setEditGroupModal((prev) => ({ ...prev, open: false }))}
              >
                Zamknij
              </button>
            </div>

            <form className="Form" onSubmit={handleEditGroupSubmit}>
              <div className="FormGrid">
                <label className="Field">
                  <span className="Label">Nazwa kategorii</span>
                  <input
                    className="Input"
                    name="groupName"
                    value={editGroupModal.groupName}
                    onChange={handleEditGroupModalChange}
                    required
                  />
                </label>
                <label className="Field">
                  <span className="Label">Cena</span>
                  <input
                    className="Input"
                    type="number"
                    name="price"
                    min="0"
                    step="1"
                    value={editGroupModal.price}
                    onChange={handleEditGroupModalChange}
                    required
                  />
                </label>
                <div className="Field">
                  <span className="Label">Sposób naliczania ceny</span>
                  <label className="Field InlineField">
                    <input
                      type="radio"
                      name="applyPerPerson"
                      checked={Boolean(editGroupModal.applyPerPerson)}
                      onChange={() => setEditGroupModal((prev) => ({ ...prev, applyPerPerson: true }))}
                    />
                    <span className="Label">Cena za osobę</span>
                  </label>
                  <label className="Field InlineField">
                    <input
                      type="radio"
                      name="applyPerPerson"
                      checked={!Boolean(editGroupModal.applyPerPerson)}
                      onChange={() => setEditGroupModal((prev) => ({ ...prev, applyPerPerson: false }))}
                    />
                    <span className="Label">Cena za pokój</span>
                  </label>
                </div>
              </div>

              <button className="Button" type="submit" disabled={savingGroup}>
                {savingGroup ? "Zapisywanie..." : "Zapisz kategorię"}
              </button>
            </form>
          </div>
        </div>
      )}


      <Navbar menuItems={menuItems} onLogout={onLogout} userRole={userRole} />

      <div className="WelcomePanel">
        <p className="Badge">Cennik</p>
        <h1 className="Title">Ustawienia cen</h1>
        <p className="Subtitle">Zalogowano jako {userEmail}.</p>

        <div className="BookingSection">
          <div className="TableSection">
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 16
              }}
            >
              {/* Button to open popup for adding/editing price list groups */}
              <button
                className="Button"
                type="button"
                onClick={() => setShowPriceListPopup(true)}
              >
                Dodaj nową kategorię +
              </button>

              <div className="FormHeader" style={{ margin: 0 }}>
                <h2 className="SectionTitle">Pokoje ({roomCount}/{ROOM_LIMIT})</h2>
                <p className="Muted">Max {ROOM_LIMIT} pozycji.</p>
              </div>
            </div>

            {/* Close the TableSection div properly after the popup button */}

            {roomRowsByGroup.length === 0 ? (
              <div className="TableWrap">
                <table className="DataTable">
                  <tbody>
                    <tr>
                      <td>Brak pozycji.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              roomRowsByGroup.map(([groupName, groupRooms]) => {
                const groupPrice = roomItems.find(
                  (room) => String(room.groupName || "").trim() === groupName
                )?.price ?? "";
                return (
                  <div key={groupName} className="TableWrap" style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                        marginBottom: 8
                      }}
                    >
                      <h3 className="SectionTitle" style={{ margin: 0 }}>
                        Kategoria: {groupName}
                      </h3>
                      <button
                        className="Button Ghost"
                        type="button"
                        onClick={() => openEditGroupModal(groupName)}
                        disabled={savingGroup}
                      >
                        Edytuj Kategorię
                      </button>
                    </div>
                    <table className="DataTable">
                      <thead>
                        <tr>
                          <th>CENA</th>
                          <th>NAZWA</th>
                          <th>Za osobę</th>
                          <th>Limit osób</th>
                          <th>Zapisz</th>
                          <th>Usuń</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupRooms.map((room) => (
                          <tr key={room.id}>
                            <td>
                              <span>{groupPrice}</span>
                            </td>
                            <td>
                              <input
                                className="Input"
                                value={room.editName}
                                onChange={(event) =>
                                  handleRoomEditChange(room.id, "name", event.target.value)
                                }
                              />
                            </td>
                            <td>
                              <span>{Boolean(room.editApplyPerPerson) ? "TAK" : "NIE"}</span>
                            </td>
                            <td>
                              <input
                                className="Input"
                                type="number"
                                min="1"
                                step="1"
                                style={{ width: "6ch" }}
                                value={room.editPersonLimit}
                                onChange={(event) =>
                                  handleRoomEditChange(
                                    room.id,
                                    "personLimit",
                                    event.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <button
                                className="Button Ghost"
                                type="button"
                                onClick={() => handleRoomUpdate(room)}
                                disabled={savingRoom}
                              >
                                Zapisz
                              </button>
                            </td>
                            <td>
                              <button
                                className="Button Ghost DangerButton"
                                type="button"
                                onClick={() => handleRoomDelete(room.id)}
                                disabled={savingRoom}
                              >
                                Usuń
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })
            )}
          </div>

          <div className="TableSection">
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap"
              }}
            >
              <button
                className="Button"
                type="button"
                onClick={() => setShowReservationPopup(true)}
                disabled={savingReservation || isReservationLimitReached}
                style={{ width: "auto" }}
              >
                {savingReservation ? "Zapisywanie..." : "Dodaj pozycję +"}
              </button>
              <h2 className="SectionTitle" style={{ margin: 0 }}>
                Pozycja do rezerwacji ({reservationCount}/{RESERVATION_LIMIT})
              </h2>
              <p className="Muted" style={{ margin: 0 }}>
                Max {RESERVATION_LIMIT} pozycji.
              </p>
            </div>

            {reservationStatus.message ? (
              <p
                className={reservationStatus.type === "error" ? "Error" : "Success"}
              >
                {reservationStatus.message}
              </p>
            ) : null}

            <div className="TableWrap">
              <table className="DataTable">
                <thead>
                  <tr>
                    <th>NAZWA</th>
                    <th>CENA</th>
                    <th>Za dobę</th>
                    <th>Za osobę</th>
                    <th>Zapisz</th>
                    <th>Usuń</th>
                  </tr>
                </thead>
                <tbody>
                  {reservationRows.length === 0 ? (
                    <tr>
                      <td colSpan="6">Brak pozycji.</td>
                    </tr>
                  ) : (
                    reservationRows.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <input
                            className="Input"
                            value={item.editName}
                            onChange={(event) =>
                              handleReservationEditChange(
                                item.id,
                                "name",
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="Input"
                            type="number"
                            min="0"
                            step="1"
                            style={{ width: "6ch" }}
                            value={item.editPrice}
                            onChange={(event) =>
                              handleReservationEditChange(
                                item.id,
                                "price",
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={Boolean(item.editApplyPerDay)}
                            onChange={(event) =>
                              handleReservationEditChange(
                                item.id,
                                "applyPerDay",
                                event.target.checked
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={Boolean(item.editApplyPerPerson)}
                            onChange={(event) =>
                              handleReservationEditChange(
                                item.id,
                                "applyPerPerson",
                                event.target.checked
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            className="Button Ghost"
                            type="button"
                            onClick={() => handleReservationUpdate(item)}
                            disabled={savingReservation}
                          >
                            Zapisz
                          </button>
                        </td>
                        <td>
                          <button
                            className="Button Ghost DangerButton"
                            type="button"
                            onClick={() => handleReservationDelete(item.id)}
                            disabled={savingReservation}
                          >
                            Usuń
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="TableSection">
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap"
              }}
            >
              <button
                className="Button"
                type="button"
                onClick={() => setShowPaymentPopup(true)}
                disabled={savingPayment || isPaymentLimitReached}
                style={{ width: "auto" }}
              >
                {savingPayment ? "Zapisywanie..." : "Dodaj metodę +"}
              </button>
              <h2 className="SectionTitle" style={{ margin: 0 }}>
                Metody płatności ({paymentCount}/{PAYMENT_LIMIT})
              </h2>
              <p className="Muted" style={{ margin: 0 }}>
                Max {PAYMENT_LIMIT} pozycji.
              </p>
            </div>

            {paymentStatus.message ? (
              <p className={paymentStatus.type === "error" ? "Error" : "Success"}>
                {paymentStatus.message}
              </p>
            ) : null}

            <div className="TableWrap">
              <table className="DataTable">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th>Procent zadatku</th>
                    <th>Termin płatności (dni)</th>
                    <th>Domyślna</th>
                    <th>Zapisz</th>
                    <th>Usuń</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentRows.length === 0 ? (
                    <tr>
                      <td colSpan="6">Brak metod.</td>
                    </tr>
                  ) : (
                    paymentRows.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <input
                            className="Input"
                            value={item.editName}
                            onChange={(event) =>
                              handlePaymentEditChange(
                                item.id,
                                "name",
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="Input"
                            type="number"
                            min="0"
                            step="1"
                            style={{ width: "6ch" }}
                            value={item.editDepositPercent}
                            onChange={(event) =>
                              handlePaymentEditChange(
                                item.id,
                                "depositPercent",
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="Input"
                            type="number"
                            min="0"
                            step="1"
                            style={{ width: "6ch" }}
                            value={item.editPaymentDueDays}
                            onChange={(event) =>
                              handlePaymentEditChange(
                                item.id,
                                "paymentDueDays",
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={Boolean(item.editIsDefault)}
                            onChange={(event) =>
                              handlePaymentEditChange(
                                item.id,
                                "isDefault",
                                event.target.checked
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            className="Button Ghost"
                            type="button"
                            onClick={() => handlePaymentUpdate(item)}
                            disabled={savingPayment}
                          >
                            Zapisz
                          </button>
                        </td>
                        <td>
                          <button
                            className="Button Ghost DangerButton"
                            type="button"
                            onClick={() => handlePaymentDelete(item.id)}
                            disabled={savingPayment}
                          >
                            Usuń
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default PriceListAdm;

function ModalAddReservationItem({ onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: "",
    price: "",
    applyPerDay: true,
    applyPerPerson: false
  });

  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const success = await onSave(form);
    if (success) {
      onClose();
    }
  };

  return (
    <div className="ModalOverlay" onClick={onClose}>
      <div className="ModalPanel" onClick={(event) => event.stopPropagation()}>
        <div className="ModalHeader">
          <h2 className="SectionTitle">Dodaj pozycję do rezerwacji</h2>
          <button className="Button Ghost" type="button" onClick={onClose}>
            Zamknij
          </button>
        </div>
        <form className="Form" onSubmit={handleSubmit}>
          <div className="FormGrid">
            <label className="Field">
              <span className="Label">NAZWA</span>
              <input
                className="Input"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </label>
            <label className="Field">
              <span className="Label">CENA</span>
              <input
                className="Input"
                type="number"
                name="price"
                min="0"
                step="1"
                value={form.price}
                onChange={handleChange}
                required
              />
            </label>
            <label className="Field InlineField">
              <input
                type="checkbox"
                name="applyPerDay"
                checked={form.applyPerDay}
                onChange={handleChange}
              />
              <span className="Label">Za dobę</span>
            </label>
            <label className="Field InlineField">
              <input
                type="checkbox"
                name="applyPerPerson"
                checked={form.applyPerPerson}
                onChange={handleChange}
              />
              <span className="Label">Za osobę</span>
            </label>
          </div>
          <button className="Button" type="submit" disabled={saving}>
            {saving ? "Zapisywanie..." : "Dodaj pozycję"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ModalAddPaymentMethod({ onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: "",
    depositPercent: "",
    paymentDueDays: ""
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const success = await onSave(form);
    if (success) {
      onClose();
    }
  };

  return (
    <div className="ModalOverlay" onClick={onClose}>
      <div className="ModalPanel" onClick={(event) => event.stopPropagation()}>
        <div className="ModalHeader">
          <h2 className="SectionTitle">Dodaj metodę płatności</h2>
          <button className="Button Ghost" type="button" onClick={onClose}>
            Zamknij
          </button>
        </div>
        <form className="Form" onSubmit={handleSubmit}>
          <div className="FormGrid">
            <label className="Field">
              <span className="Label">Nazwa</span>
              <input
                className="Input"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </label>
            <label className="Field">
              <span className="Label">Procent zadatku</span>
              <input
                className="Input"
                type="number"
                name="depositPercent"
                min="0"
                step="1"
                value={form.depositPercent}
                onChange={handleChange}
                required
              />
            </label>
            <label className="Field">
              <span className="Label">Termin płatności (dni)</span>
              <input
                className="Input"
                type="number"
                name="paymentDueDays"
                min="0"
                step="1"
                value={form.paymentDueDays}
                onChange={handleChange}
                required
              />
            </label>
          </div>
          <button className="Button" type="submit" disabled={saving}>
            {saving ? "Zapisywanie..." : "Dodaj metodę"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ModalAddGroup({ onClose, onSaved }) {
  const [groupName, setGroupName] = useState("");
  const [price, setPrice] = useState("");
  const [applyPerPerson, setApplyPerPerson] = useState(false);
  const [rooms, setRooms] = useState([{ name: "", personLimit: "" }]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const handleAddRoom = () => {
    setRooms((prev) => [...prev, { name: "", personLimit: "" }]);
  };
  const handleRemoveRoom = (idx) => {
    setRooms((prev) => prev.filter((_, i) => i !== idx));
  };
  const handleRoomChange = (idx, field, value) => {
    setRooms((prev) => prev.map((room, i) => i === idx ? { ...room, [field]: value } : room));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });
    const finalGroupName = groupName;
    if (!finalGroupName.trim()) {
      setStatus({ type: "error", message: "Podaj lub wybierz nazwę kategorii." });
      return;
    }
    if (!price || isNaN(Number(price))) {
      setStatus({ type: "error", message: "Podaj poprawną cenę." });
      return;
    }
    for (const room of rooms) {
      if (!room.name.trim()) {
        setStatus({ type: "error", message: "Podaj nazwę pokoju." });
        return;
      }
      if (!room.personLimit || isNaN(Number(room.personLimit)) || Number(room.personLimit) <= 0) {
        setStatus({ type: "error", message: "Podaj poprawny limit osób." });
        return;
      }
    }
    setSaving(true);
    try {
      for (const room of rooms) {
        await addDoc(collection(db, "priceList"), {
          type: "room",
          groupName: finalGroupName.trim(),
          name: room.name.trim(),
          price: Number(price),
          applyPerPerson,
          personLimit: Number(room.personLimit),
          createdAt: serverTimestamp()
        });
      }
      setStatus({ type: "success", message: "Dodano pozycję." });
      if (onSaved) onSaved();
    } catch (error) {
      setStatus({ type: "error", message: error.message || "Nie udało się dodać pozycji." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ModalOverlay" onClick={onClose}>
      <div className="ModalPanel" onClick={e => e.stopPropagation()}>
        <div className="ModalHeader">
          <h2 className="SectionTitle">Dodaj kategorię i pokoje</h2>
          <button className="Button Ghost" type="button" onClick={onClose}>
            Zamknij
          </button>
        </div>

<div className="InstructionBox" style={{ margin: '15px 0', padding: '15px', backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', lineHeight: '1.5' }}>
      <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
        <li style={{ marginBottom: '6px' }}><strong>Nazwa kategorii:</strong> Wpisz nazwę</li>
        <li style={{ marginBottom: '6px' }}><strong>Cena:</strong> Wpisz kwotę</li>
        <li style={{ marginBottom: '6px' }}><strong>Cena za osobę:</strong> Zaznacz, jeśli cena dotyczy 1 osoby (nie całego pokoju)</li>
        <li style={{ marginBottom: '6px' }}><strong>Nazwa pokoju / Limit osób:</strong> Wpisz dane pojedynczego pokoju</li>
        <li style={{ marginBottom: '6px' }}><strong>Dodaj pokój:</strong> Kliknij, aby zatwierdzić pokój i wpisać kolejny</li>
        <li style={{ marginBottom: '6px' }}><strong>Zapisz grupę i pokoje:</strong> Kliknij na końcu, aby zapisać wszystko</li>
        <li style={{ marginBottom: '0px' }}><strong>Zamknij:</strong> Wyjdź bez zapisywania zmian</li>
      </ul>
    </div>

        <form className="Form" onSubmit={handleSubmit}>
          <div className="FormGrid">
            <label className="Field">
              <span className="Label">Nazwa kategorii</span>
              <input
                className="Input"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                required
              />
            </label>
            <label className="Field">
              <span className="Label">Cena (PLN)</span>
              <input
                className="Input"
                type="number"
                min="0"
                step="1"
                value={price}
                onChange={e => setPrice(e.target.value)}
                required
              />
            </label>
            <div className="Field">
              <span className="Label">Sposób naliczania ceny</span>
              <label className="Field InlineField">
                <input
                  type="radio"
                  name="applyPerPerson"
                  checked={applyPerPerson}
                  onChange={() => setApplyPerPerson(true)}
                />
                <span className="Label">Cena za osobę</span>
              </label>
              <label className="Field InlineField">
                <input
                  type="radio"
                  name="applyPerPerson"
                  checked={!applyPerPerson}
                  onChange={() => setApplyPerPerson(false)}
                />
                <span className="Label">Cena za pokój</span>
              </label>
            </div>
          </div>
          <div style={{ margin: '16px 0' }}>
            <span className="Label">Pokoje w kategorii</span>
            {rooms.map((room, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <input
                  className="Input"
                  placeholder="Nazwa pokoju"
                  value={room.name}
                  onChange={e => handleRoomChange(idx, "name", e.target.value)}
                  required
                  style={{ flex: 2 }}
                />
                <input
                  className="Input"
                  placeholder="Limit osób"
                  type="number"
                  min="1"
                  value={room.personLimit}
                  onChange={e => handleRoomChange(idx, "personLimit", e.target.value)}
                  required
                  style={{ flex: "0 0 7ch", width: "7ch" }}
                />
                {rooms.length > 1 && (
                  <button
                    className="Button Ghost DangerButton SmallButton"
                    type="button"
                    onClick={() => handleRemoveRoom(idx)}
                  >
                    Usuń
                  </button>
                )}
              </div>
            ))}
            <button className="Button Ghost SmallButton" type="button" onClick={handleAddRoom}>
              Dodaj pokój
            </button>
          </div>
          {status.message && (
            <p className={status.type === "error" ? "Error" : "Success"}>{status.message}</p>
          )}
          <button className="Button" type="submit" disabled={saving}>
            {saving ? "Zapisywanie..." : "Zapisz kategorię i pokoje"}
          </button>
        </form>
      </div>
    </div>
  );
}
